import { Clipboard } from '@capacitor/clipboard'
import { Capacitor } from '@capacitor/core'

/**
 * Универсальная функция копирования текста в буфер обмена
 * Работает как в браузере, так и в мобильных приложениях (Capacitor)
 * @param {string} text - Текст для копирования
 * @returns {Promise<void>}
 */
export async function copyToClipboard(text) {
  try {
    // Используем Capacitor Clipboard API (работает везде - в браузере и в приложениях)
    await Clipboard.write({
      string: text
    })
  } catch (error) {
    // Fallback на стандартный API, если Capacitor не доступен
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
    } else {
      // Старый метод для совместимости
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
      } finally {
        document.body.removeChild(textArea)
      }
    }
  }
}

/**
 * Копирование текста, который ещё не получен (ответ запроса). Safari пускает запись в буфер только
 * синхронно внутри жеста пользователя — после await сетевого запроса writeText отвечает NotAllowedError.
 * ClipboardItem принимает обещание: запись начинается в жесте, а данные приходят позже. В нативной
 * сборке и без ClipboardItem — ждём текст и копируем обычным путём.
 * @param {Promise<string>} textPromise
 * @returns {Promise<void>}
 */
export async function copyTextWhenReady(textPromise, { native = Capacitor.isNativePlatform() } = {}) {
  const canWriteItem =
    !native && typeof window.ClipboardItem === 'function' && navigator.clipboard && navigator.clipboard.write
  if (!canWriteItem) {
    await copyToClipboard(await textPromise)
    return
  }
  const blob = textPromise.then((text) => new Blob([text], { type: 'text/plain' }))
  await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': blob })])
}

/**
 * Получить текст из буфера обмена
 * @returns {Promise<string>}
 */
export async function readFromClipboard() {
  try {
    const result = await Clipboard.read()
    return result.value
  } catch (error) {
    // Fallback на стандартный API
    if (navigator.clipboard && navigator.clipboard.readText) {
      return await navigator.clipboard.readText()
    }
    return ''
  }
}

